import { reportMetric, startTimer } from '#pplx/frontend/metrics/browser.js';
import { streamEvents } from '#pplx/frontend/stream/events.js';
import { isComputerQuerySource } from '#pplx/frontend/stream/types.js';
const COMPUTER_TASK_PATH_FRAGMENT = '/computer/tasks';
const TASK_INPUT_SUBMITTED = 'web.frontend.computer_task.timing.input_submitted';
const TASK_CREATED = 'web.frontend.computer_task.timing.task_created';
const TASK_PAGE_NAVIGATED = 'web.frontend.computer_task.timing.page_navigated';
const TASK_FIRST_CONTENT_RENDERED = 'web.frontend.computer_task.timing.first_content_rendered';
const TASK_STREAM_FAILED = 'web.frontend.computer_task.timing.stream_failed';
const ASI_LIVE_ASK_STREAM_VERSION_KEY = 'asi_live_ask_stream_version';
const TASK_BOOTSTRAP_STREAM_OPENED = 'web.frontend.computer_task.timing.bootstrap_stream.opened';
const TASK_BOOTSTRAP_STREAM_FIRST_MESSAGE = 'web.frontend.computer_task.timing.bootstrap_stream.first_message';
const TASK_BOOTSTRAP_STREAM_END = 'web.frontend.computer_task.timing.bootstrap_stream.end';
const TASK_SUBSEQUENT_STREAM_OPENED = 'web.frontend.computer_task.timing.subsequent_stream.opened';
const TASK_SUBSEQUENT_STREAM_FIRST_MESSAGE = 'web.frontend.computer_task.timing.subsequent_stream.first_message';
const TASK_SUBSEQUENT_STREAM_END = 'web.frontend.computer_task.timing.subsequent_stream.end';
const SSE_CONNECTION_OPENED = 'web.sse.connection_opened';
const SSE_FIRST_MESSAGE_SEEN = 'web.sse.first_message_seen';
const SSE_END_OF_STREAM = 'web.sse.end_of_stream';
const CLEANUP_DELAY_MS = 5 * 60 * 1000;
const MAX_COMPUTER_TASK_TIMING_DURATION_MS = 24 * 60 * 60 * 1000;
const logicalTimingsById = new Map();
const streamTimingsById = new Map();
const pendingPageNavigationsById = new Map();
let hasSubscribedToStreamEvents = false;
function getAsiLiveAskStreamMetricContext(message) {
    const version = message?.telemetry_data?.asi_live_ask_stream_version;
    if (version !== 'v2')
        return {};
    return { [ASI_LIVE_ASK_STREAM_VERSION_KEY]: version };
}
function isSubmitParams(params) {
    return typeof params === 'object' && params != null && 'frontend_uuid' in params;
}
function isReconnectParams(params) {
    return typeof params === 'object' && params != null && 'resume_entry_uuid' in params;
}
function getWindowPathname() {
    return typeof window === 'undefined' ? '' : window.location.pathname;
}
function isComputerTaskPath() {
    return getWindowPathname().includes(COMPUTER_TASK_PATH_FRAGMENT);
}
function getAbsoluteNow() {
    if (typeof performance === 'undefined')
        return Date.now();
    const timeOrigin = typeof performance.timeOrigin === 'number'
        ? performance.timeOrigin
        : Date.now() - performance.now();
    return timeOrigin + performance.now();
}
function indexLogicalTiming(logical, id) {
    if (!id)
        return;
    logical.ids.add(id);
    logicalTimingsById.set(id, logical);
    flushPendingPageNavigation(logical, id);
}
function getLogicalTimingByIds(ids) {
    for (const id of ids) {
        if (!id)
            continue;
        const logical = logicalTimingsById.get(id);
        if (logical)
            return logical;
    }
    return undefined;
}
function scheduleCleanup(logical) {
    if (logical.cleanupTimeoutId) {
        clearTimeout(logical.cleanupTimeoutId);
    }
    logical.cleanupTimeoutId = setTimeout(() => {
        for (const id of logical.ids) {
            logicalTimingsById.delete(id);
        }
    }, CLEANUP_DELAY_MS);
}
function cancelCleanup(logical) {
    if (!logical.cleanupTimeoutId)
        return;
    clearTimeout(logical.cleanupTimeoutId);
    delete logical.cleanupTimeoutId;
}
function clearPendingPageNavigation(id) {
    const pendingPageNavigation = pendingPageNavigationsById.get(id);
    if (pendingPageNavigation?.cleanupTimeoutId) {
        clearTimeout(pendingPageNavigation.cleanupTimeoutId);
    }
    pendingPageNavigationsById.delete(id);
}
function setPendingPageNavigation(id, pendingPageNavigation) {
    clearPendingPageNavigation(id);
    pendingPageNavigationsById.set(id, {
        ...pendingPageNavigation,
        cleanupTimeoutId: setTimeout(() => {
            pendingPageNavigationsById.delete(id);
        }, CLEANUP_DELAY_MS),
    });
}
function getStreamId(stream) {
    return stream.id.description;
}
function getNextStreamAttempt({ logical, connectionType, }) {
    if (connectionType === 'bootstrap')
        return 0;
    return Math.max(1, logical.attempts);
}
function attachStreamTimingToLogical(streamTiming, logical) {
    if (!streamTiming.hasAttachedLogical) {
        streamTiming.attempt = getNextStreamAttempt({
            logical,
            connectionType: streamTiming.connectionType,
        });
        logical.attempts = Math.max(logical.attempts, streamTiming.attempt + 1);
        streamTiming.hasAttachedLogical = true;
        logical.streamTimings.push(streamTiming);
    }
    streamTiming.logical = logical;
    cancelCleanup(logical);
}
function getMessageContext(message) {
    return {
        backend_uuid: message?.backend_uuid,
        context_uuid: message?.context_uuid,
        frontend_context_uuid: message?.frontend_context_uuid,
        frontend_uuid: message?.frontend_uuid,
        status: message?.status,
        thread_url_slug: message?.thread_url_slug,
        ...getAsiLiveAskStreamMetricContext(message),
    };
}
function getStreamContext(stream, streamTiming, message) {
    const params = stream.params;
    const submitParams = isSubmitParams(params) ? params : undefined;
    return {
        ...getMessageContext(message),
        stream_id: getStreamId(stream),
        connection_type: streamTiming.connectionType,
        attempt: streamTiming.attempt,
        is_reconnect: streamTiming.connectionType === 'reconnect',
        query_source: submitParams?.query_source,
        mode: submitParams?.mode,
    };
}
function getLogicalContext(logical) {
    return {
        frontend_context_uuid: logical.frontendContextUuid,
        backend_uuid: logical.entryUuid,
        context_uuid: logical.contextUuid,
        thread_url_slug: logical.threadUrlSlug,
    };
}
function getStreamLabelPrefix(streamTiming) {
    if (streamTiming.connectionType === 'bootstrap')
        return 'Bootstrap Stream';
    return `Subsequent Stream ${streamTiming.attempt || 1}`;
}
function getStreamTimingMetricName({ streamTiming, eventMetricName, }) {
    if (streamTiming.connectionType === 'bootstrap') {
        switch (eventMetricName) {
            case SSE_CONNECTION_OPENED:
                return TASK_BOOTSTRAP_STREAM_OPENED;
            case SSE_FIRST_MESSAGE_SEEN:
                return TASK_BOOTSTRAP_STREAM_FIRST_MESSAGE;
            case SSE_END_OF_STREAM:
                return TASK_BOOTSTRAP_STREAM_END;
            default:
                return undefined;
        }
    }
    switch (eventMetricName) {
        case SSE_CONNECTION_OPENED:
            return TASK_SUBSEQUENT_STREAM_OPENED;
        case SSE_FIRST_MESSAGE_SEEN:
            return TASK_SUBSEQUENT_STREAM_FIRST_MESSAGE;
        case SSE_END_OF_STREAM:
            return TASK_SUBSEQUENT_STREAM_END;
        default:
            return undefined;
    }
}
function getStreamTimingLabel({ streamTiming, eventMetricName, }) {
    const labelPrefix = getStreamLabelPrefix(streamTiming);
    switch (eventMetricName) {
        case SSE_CONNECTION_OPENED:
            return `${labelPrefix} Opened`;
        case SSE_FIRST_MESSAGE_SEEN:
            return `${labelPrefix} First Message`;
        case SSE_END_OF_STREAM:
            return `${labelPrefix} End`;
        default:
            return undefined;
    }
}
function addLogicalTiming({ logical, metricName, label, context = {}, onceKey = metricName, }) {
    if (logical.debugEventKeys.has(onceKey))
        return;
    logical.debugEventKeys.add(onceKey);
    const event = logical.timer.addTiming(metricName, context);
    logical.debugEvents.push({ ...event, label });
}
function reportLogicalTimingAtDuration({ logical, metricName, duration, label, context, onceKey, }) {
    if (logical.debugEventKeys.has(onceKey))
        return;
    logical.debugEventKeys.add(onceKey);
    const startTime = logical.timer.getAbsoluteStartTime();
    const event = {
        metricName,
        duration,
        label,
    };
    logical.debugEvents.push(event);
    reportMetric(metricName, {
        ...context,
        startTime,
        duration,
    });
}
function reportStreamTimingAtDuration({ logical, streamTiming, metricName, duration, label, context, onceKey, }) {
    if (streamTiming.debugEventKeys.has(onceKey))
        return;
    streamTiming.debugEventKeys.add(onceKey);
    const startTime = logical.timer.getAbsoluteStartTime();
    streamTiming.debugEvents.push({
        metricName,
        duration,
        label,
    });
    reportMetric(metricName, {
        ...context,
        startTime,
        duration,
    });
}
function getTimingEventAbsoluteTime({ event, timer, }) {
    return event.absoluteTime ?? timer.getAbsoluteStartTime() + event.duration;
}
function getLogicalDurationForStreamEvent({ logical, stream, streamEvent, }) {
    const duration = getTimingEventAbsoluteTime({ event: streamEvent, timer: stream.timer }) -
        logical.timer.getAbsoluteStartTime();
    if (!Number.isFinite(duration))
        return undefined;
    return Math.min(Math.max(0, duration), MAX_COMPUTER_TASK_TIMING_DURATION_MS);
}
function recordPageNavigated({ logical, threadUrlSlug, duration, }) {
    if (threadUrlSlug) {
        logical.threadUrlSlug = threadUrlSlug;
        logical.ids.add(threadUrlSlug);
        logicalTimingsById.set(threadUrlSlug, logical);
    }
    const context = {
        ...getLogicalContext(logical),
        thread_url_slug: threadUrlSlug ?? logical.threadUrlSlug,
    };
    if (duration == null) {
        addLogicalTiming({
            logical,
            metricName: TASK_PAGE_NAVIGATED,
            label: 'Page Navigated',
            context,
        });
    }
    else {
        reportLogicalTimingAtDuration({
            logical,
            metricName: TASK_PAGE_NAVIGATED,
            duration: Math.max(0, duration),
            label: 'Page Navigated',
            context,
            onceKey: TASK_PAGE_NAVIGATED,
        });
    }
    for (const id of logical.ids) {
        clearPendingPageNavigation(id);
    }
}
function flushPendingPageNavigation(logical, id) {
    const pendingPageNavigation = pendingPageNavigationsById.get(id);
    if (!pendingPageNavigation)
        return;
    pendingPageNavigationsById.delete(id);
    recordPageNavigated({
        logical,
        threadUrlSlug: pendingPageNavigation.threadUrlSlug ?? id,
        duration: pendingPageNavigation.absoluteTime - logical.timer.getAbsoluteStartTime(),
    });
}
function recordStreamTimingEvent({ logical, stream, streamTiming, eventMetricName, context, }) {
    const streamEvent = stream.timer
        .getEvents()
        .find((event) => event.metricName === eventMetricName);
    if (!streamEvent)
        return;
    const metricName = getStreamTimingMetricName({ streamTiming, eventMetricName });
    const label = getStreamTimingLabel({ streamTiming, eventMetricName });
    if (!metricName || !label)
        return;
    const logicalDuration = getLogicalDurationForStreamEvent({
        logical,
        stream,
        streamEvent,
    });
    if (logicalDuration == null)
        return;
    reportStreamTimingAtDuration({
        logical,
        streamTiming,
        metricName,
        duration: logicalDuration,
        label,
        context,
        onceKey: eventMetricName,
    });
}
function recordAvailableStreamTimingEvents({ logical, stream, streamTiming, context, eventMetricNames, }) {
    for (const eventMetricName of eventMetricNames) {
        recordStreamTimingEvent({
            logical,
            stream,
            streamTiming,
            eventMetricName,
            context,
        });
    }
}
function aliasLogicalTimingFromMessage(logical, message) {
    logical.entryUuid = logical.entryUuid ?? message.backend_uuid ?? undefined;
    logical.contextUuid = logical.contextUuid ?? message.context_uuid ?? undefined;
    logical.threadUrlSlug = logical.threadUrlSlug ?? message.thread_url_slug ?? undefined;
    indexLogicalTiming(logical, message.backend_uuid);
    indexLogicalTiming(logical, message.context_uuid);
    indexLogicalTiming(logical, message.frontend_context_uuid);
    indexLogicalTiming(logical, message.frontend_uuid);
    indexLogicalTiming(logical, message.thread_url_slug);
    logical.asiLiveAskStreamMetricContext = {
        ...logical.asiLiveAskStreamMetricContext,
        ...getAsiLiveAskStreamMetricContext(message),
    };
}
export function startComputerTaskLogicalTiming({ frontendContextUuid, querySource, mode = 'asi', }) {
    const existing = logicalTimingsById.get(frontendContextUuid);
    if (existing) {
        return;
    }
    const logical = {
        timer: startTimer({
            frontend_context_uuid: frontendContextUuid,
            query_source: querySource,
            mode,
        }),
        debugEvents: [],
        debugEventKeys: new Set(),
        streamTimings: [],
        asiLiveAskStreamMetricContext: {
            [ASI_LIVE_ASK_STREAM_VERSION_KEY]: 'v1',
        },
        ids: new Set(),
        frontendContextUuid,
        attempts: 0,
    };
    logical.ids.add(frontendContextUuid);
    logicalTimingsById.set(frontendContextUuid, logical);
    addLogicalTiming({
        logical,
        metricName: TASK_INPUT_SUBMITTED,
        label: 'Input Submitted',
    });
    flushPendingPageNavigation(logical, frontendContextUuid);
}
export function markComputerTaskCreated({ frontendContextUuid, querySource, }) {
    const logical = logicalTimingsById.get(frontendContextUuid);
    if (!logical)
        return;
    addLogicalTiming({
        logical,
        metricName: TASK_CREATED,
        label: 'Task Created',
        context: {
            frontend_context_uuid: frontendContextUuid,
            query_source: querySource,
        },
    });
}
export function markComputerTaskPageNavigated({ frontendContextUuid, threadUrlSlug, }) {
    const logical = getLogicalTimingByIds([frontendContextUuid, threadUrlSlug]);
    if (!logical) {
        const absoluteTime = getAbsoluteNow();
        setPendingPageNavigation(frontendContextUuid, {
            absoluteTime,
            threadUrlSlug,
        });
        if (threadUrlSlug && threadUrlSlug !== frontendContextUuid) {
            setPendingPageNavigation(threadUrlSlug, {
                absoluteTime,
                threadUrlSlug,
            });
        }
        return;
    }
    recordPageNavigated({
        logical,
        threadUrlSlug,
    });
}
export function clearComputerTaskLogicalTiming(frontendContextUuid) {
    const logical = logicalTimingsById.get(frontendContextUuid);
    if (!logical)
        return;
    if (logical.cleanupTimeoutId) {
        clearTimeout(logical.cleanupTimeoutId);
    }
    for (const id of logical.ids) {
        logicalTimingsById.delete(id);
    }
}
export function registerComputerTaskStream(stream) {
    const streamId = getStreamId(stream);
    const params = stream.params;
    const submitParams = isSubmitParams(params) ? params : undefined;
    const reconnectParams = isReconnectParams(params) ? params : undefined;
    const logical = getLogicalTimingByIds([
        submitParams?.frontend_context_uuid,
        submitParams?.frontend_uuid,
        reconnectParams?.resume_entry_uuid,
        stream.entryId,
    ]);
    const isComputerTaskStream = logical != null ||
        isComputerTaskPath() ||
        (submitParams?.mode === 'asi' && isComputerQuerySource(submitParams.query_source));
    if (!isComputerTaskStream)
        return;
    const connectionType = stream.isReconnect ? 'reconnect' : 'bootstrap';
    const streamTiming = {
        streamId,
        connectionType,
        attempt: stream.isReconnect ? 1 : 0,
        debugEvents: [],
        debugEventKeys: new Set(),
        logical,
    };
    if (logical) {
        attachStreamTimingToLogical(streamTiming, logical);
    }
    streamTimingsById.set(streamId, streamTiming);
}
export function markComputerTaskStreamProgress({ stream, message, }) {
    const streamTiming = streamTimingsById.get(getStreamId(stream));
    if (!streamTiming)
        return;
    const logical = streamTiming.logical ?? getLogicalTimingByStreamAndMessage({ stream, message });
    if (logical) {
        attachStreamTimingToLogical(streamTiming, logical);
        aliasLogicalTimingFromMessage(logical, message);
    }
    const context = getStreamContext(stream, streamTiming, message);
    if (logical) {
        recordAvailableStreamTimingEvents({
            logical,
            stream,
            streamTiming,
            context,
            eventMetricNames: [SSE_CONNECTION_OPENED, SSE_FIRST_MESSAGE_SEEN],
        });
    }
}
export function markComputerTaskStreamOpened({ stream, }) {
    const streamTiming = streamTimingsById.get(getStreamId(stream));
    if (!streamTiming)
        return;
    const logical = streamTiming.logical ?? getLogicalTimingByStreamAndMessage({ stream });
    if (logical) {
        attachStreamTimingToLogical(streamTiming, logical);
        recordAvailableStreamTimingEvents({
            logical,
            stream,
            streamTiming,
            context: getStreamContext(stream, streamTiming),
            eventMetricNames: [SSE_CONNECTION_OPENED],
        });
    }
}
export function markComputerTaskStreamCompleted({ stream, message, }) {
    const streamId = getStreamId(stream);
    const streamTiming = streamTimingsById.get(streamId);
    if (!streamTiming)
        return;
    const logical = streamTiming.logical ??
        (message ? getLogicalTimingByStreamAndMessage({ stream, message }) : undefined);
    if (logical && message) {
        attachStreamTimingToLogical(streamTiming, logical);
        aliasLogicalTimingFromMessage(logical, message);
    }
    const context = getStreamContext(stream, streamTiming, message);
    if (logical) {
        recordAvailableStreamTimingEvents({
            logical,
            stream,
            streamTiming,
            context,
            eventMetricNames: [SSE_CONNECTION_OPENED, SSE_FIRST_MESSAGE_SEEN, SSE_END_OF_STREAM],
        });
        scheduleCleanup(logical);
    }
    streamTimingsById.delete(streamId);
}
export function markComputerTaskStreamFailed({ stream, message, error, }) {
    const streamId = getStreamId(stream);
    const streamTiming = streamTimingsById.get(streamId);
    if (!streamTiming)
        return;
    const logical = streamTiming.logical ??
        (message ? getLogicalTimingByStreamAndMessage({ stream, message }) : undefined);
    if (logical && message) {
        attachStreamTimingToLogical(streamTiming, logical);
        aliasLogicalTimingFromMessage(logical, message);
    }
    const context = {
        ...getStreamContext(stream, streamTiming, message),
        error_name: error.name,
    };
    if (logical) {
        recordAvailableStreamTimingEvents({
            logical,
            stream,
            streamTiming,
            context,
            eventMetricNames: [SSE_CONNECTION_OPENED, SSE_FIRST_MESSAGE_SEEN],
        });
        addLogicalTiming({
            logical,
            metricName: TASK_STREAM_FAILED,
            label: `${getStreamLabelPrefix(streamTiming)} Failed`,
            context,
            onceKey: `${streamId}:failed`,
        });
        scheduleCleanup(logical);
    }
    streamTimingsById.delete(streamId);
}
function getLogicalTimingByStreamAndMessage({ stream, message, }) {
    const params = stream.params;
    const submitParams = isSubmitParams(params) ? params : undefined;
    const reconnectParams = isReconnectParams(params) ? params : undefined;
    return getLogicalTimingByIds([
        message?.backend_uuid,
        message?.context_uuid,
        message?.frontend_context_uuid,
        message?.frontend_uuid,
        message?.thread_url_slug,
        submitParams?.frontend_context_uuid,
        submitParams?.frontend_uuid,
        reconnectParams?.resume_entry_uuid,
        stream.entryId,
    ]);
}
function isPotentialComputerTaskStream(stream) {
    const params = stream.params;
    const submitParams = isSubmitParams(params) ? params : undefined;
    return (streamTimingsById.has(getStreamId(stream)) ||
        isComputerTaskPath() ||
        (submitParams?.mode === 'asi' && isComputerQuerySource(submitParams.query_source)));
}
function getComputerTaskStreamTimingDebugSnapshot(stream, message) {
    if (!isPotentialComputerTaskStream(stream))
        return undefined;
    const streamTiming = streamTimingsById.get(getStreamId(stream)) ?? {
        streamId: getStreamId(stream),
        connectionType: stream.isReconnect ? 'reconnect' : 'bootstrap',
        attempt: stream.isReconnect ? 1 : 0,
        debugEvents: [],
        debugEventKeys: new Set(),
    };
    const events = stream.timer.getEvents().flatMap((event) => {
        const label = getStreamTimingLabel({
            streamTiming,
            eventMetricName: event.metricName,
        });
        if (!label)
            return [];
        return [{ ...event, label }];
    });
    return {
        events,
        startTime: stream.timer.getAbsoluteStartTime(),
        source: 'stream',
        contextUuid: stream.threadId ?? message?.context_uuid,
        entryUuid: stream.entryId ?? message?.backend_uuid,
    };
}
export function getComputerTaskTimingDebugSnapshot({ stream, message, }) {
    const logical = getLogicalTimingByStreamAndMessage({ stream, message });
    if (!logical)
        return getComputerTaskStreamTimingDebugSnapshot(stream, message);
    const events = [
        ...logical.debugEvents,
        ...logical.streamTimings.flatMap((streamTiming) => streamTiming.debugEvents),
    ].sort((a, b) => a.duration - b.duration);
    if (events.length) {
        return {
            events,
            startTime: logical.timer.getAbsoluteStartTime(),
            source: 'logical',
            contextUuid: logical.contextUuid ?? stream.threadId ?? message?.context_uuid,
            entryUuid: logical.entryUuid ?? stream.entryId ?? message?.backend_uuid,
        };
    }
    return getComputerTaskStreamTimingDebugSnapshot(stream, message);
}
export function markComputerTaskLogicalFirstAnswerPainted(context) {
    const logical = getLogicalTimingByIds([
        context.backend_uuid,
        context.context_uuid,
        context.frontend_context_uuid,
        context.frontend_uuid,
        context.thread_url_slug,
    ]);
    if (!logical)
        return false;
    addLogicalTiming({
        logical,
        metricName: TASK_FIRST_CONTENT_RENDERED,
        label: 'First Content Rendered',
        context: {
            ...context,
            ...logical.asiLiveAskStreamMetricContext,
        },
    });
    return true;
}
function ensureComputerTaskStreamTimingSubscriptions() {
    if (hasSubscribedToStreamEvents)
        return;
    hasSubscribedToStreamEvents = true;
    streamEvents.on('created', ({ stream }) => {
        registerComputerTaskStream(stream);
    });
    streamEvents.on('opened', ({ stream }) => {
        markComputerTaskStreamOpened({ stream });
    });
    streamEvents.on('progress', ({ stream, message }) => {
        markComputerTaskStreamProgress({ stream, message });
    });
    streamEvents.on('completed', ({ stream, message }) => {
        markComputerTaskStreamCompleted({ stream, message });
    });
    streamEvents.on('error', ({ stream, message, error }) => {
        markComputerTaskStreamFailed({ stream, message, error });
    });
}
ensureComputerTaskStreamTimingSubscriptions();
//# sourceMappingURL=computerTaskTiming.js.map